import { useState, useEffect } from "react";
import {
  Card,
  message,
  Tag,
  Empty,
  Spin,
  Typography,
  Space,
  Switch,
  Table,
  Tooltip,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useAuth0 } from "../../../react-auth0-spa";
import { useAppContext } from "../../../context";
import config from "../../../api_config.json";
import allFeaturesQuery from "../queries/allFeaturesQuery";
import sellerFeaturesQuery from "../queries/sellerFeaturesQuery";
import createSellerFeatureMutation from "../queries/createSellerFeatureMutation";
import updateSellerFeatureMutation from "../queries/updateSellerFeatureMutation";
import deleteSellerFeatureMutation from "../queries/deleteSellerFeatureMutation";

const { Text } = Typography;

const PLAN_COLORS = {
  base: "default",
  verified: "blue",
  premium: "gold",
};

const MarketplaceSellerFeatures = ({ seller, onUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [allFeatures, setAllFeatures] = useState([]);
  const [sellerFeatures, setSellerFeatures] = useState([]);
  const [togglingFeature, setTogglingFeature] = useState(null);

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  const fetchAllFeatures = async () => {
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      const response = await axios.post(
        graphqlUrl,
        { query: allFeaturesQuery },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const edges = response.data?.marketplace_features?.edges || [];
      return edges.map((edge) => ({
        ...edge.node,
        plans: (edge.node.plans || []).map((p) => (typeof p === "string" ? p : p.value)),
      }));
    } catch (error) {
      console.error("Error fetching all features:", error);
      message.error("Errore durante il caricamento delle features");
      return [];
    }
  };

  const fetchSellerFeatures = async () => {
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      const response = await axios.post(
        graphqlUrl,
        {
          query: sellerFeaturesQuery,
          variables: {
            where: {
              seller_pointer: {
                have: {
                  objectId: { equalTo: seller.objectId },
                },
              },
            },
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const edges = response.data?.marketplace_seller_features?.edges || [];
      return edges.map((edge) => ({
        ...edge.node,
        feature_pointer: edge.node.feature_pointer
          ? {
              ...edge.node.feature_pointer,
              plans: (edge.node.feature_pointer.plans || []).map((p) => (typeof p === "string" ? p : p.value)),
            }
          : null,
      }));
    } catch (error) {
      console.error("Error fetching seller features:", error);
      message.error("Errore durante il caricamento delle features del seller");
      return [];
    }
  };

  const loadData = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    const [features, sf] = await Promise.all([
      fetchAllFeatures(),
      fetchSellerFeatures(),
    ]);
    setAllFeatures(features);
    setSellerFeatures(sf);
    if (showLoading) setLoading(false);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seller.objectId]);

  // Build a map: featureId -> sellerFeature record
  // Also track duplicates so we can clean them up on next toggle
  const sellerFeatureMap = {};
  const duplicateSellerFeatures = [];
  sellerFeatures.forEach((sf) => {
    if (sf.feature_pointer) {
      const fid = sf.feature_pointer.objectId;
      if (sellerFeatureMap[fid]) {
        duplicateSellerFeatures.push(sf.objectId);
      } else {
        sellerFeatureMap[fid] = sf;
      }
    }
  });

  // Check if a feature is included by the seller's plan
  const isIncludedByPlan = (feature) => {
    const plans = feature.plans || [];
    return plans.includes(seller.plan);
  };

  // Resolve effective status for a feature
  const getFeatureStatus = (feature) => {
    const sf = sellerFeatureMap[feature.objectId];
    const planIncluded = isIncludedByPlan(feature);

    if (sf) {
      // Override exists
      return {
        enabled: sf.enabled,
        source: sf.enabled
          ? planIncluded
            ? "piano + override"
            : "override (forzata)"
          : "override (disabilitata)",
        hasOverride: true,
        overrideId: sf.objectId,
        overrideEnabled: sf.enabled,
      };
    }

    // No override — use plan default
    return {
      enabled: planIncluded,
      source: planIncluded ? "piano" : "non inclusa",
      hasOverride: false,
      overrideId: null,
      overrideEnabled: null,
    };
  };

  const handleToggle = async (feature, newEnabled) => {
    setTogglingFeature(feature.objectId);
    try {
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;
      const headers = { Authorization: `Bearer ${token}` };
      const status = getFeatureStatus(feature);
      const planIncluded = isIncludedByPlan(feature);

      // Clean up any duplicate overrides
      if (duplicateSellerFeatures.length > 0) {
        await Promise.all(
          duplicateSellerFeatures.map((id) =>
            axios.post(
              graphqlUrl,
              { query: deleteSellerFeatureMutation, variables: { id } },
              { headers }
            )
          )
        );
      }

      if (status.hasOverride) {
        // Override exists
        if (newEnabled === planIncluded) {
          // New state matches plan default — remove override
          await axios.post(
            graphqlUrl,
            {
              query: deleteSellerFeatureMutation,
              variables: { id: status.overrideId },
            },
            { headers }
          );
          message.success("Override rimosso (torna al default del piano)");
        } else {
          // Update override
          await axios.post(
            graphqlUrl,
            {
              query: updateSellerFeatureMutation,
              variables: {
                id: status.overrideId,
                fields: { enabled: newEnabled },
              },
            },
            { headers }
          );
          message.success("Feature aggiornata");
        }
      } else {
        // No override — create one
        await axios.post(
          graphqlUrl,
          {
            query: createSellerFeatureMutation,
            variables: {
              fields: {
                seller_pointer: {
                  link: seller.objectId,
                },
                feature_pointer: {
                  link: feature.objectId,
                },
                enabled: newEnabled,
              },
            },
          },
          { headers }
        );
        message.success("Override creato");
      }

      await loadData(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error toggling feature:", error);
      message.error("Errore durante l'aggiornamento della feature");
    } finally {
      setTogglingFeature(null);
    }
  };

  const columns = [
    {
      title: "Feature",
      key: "feature",
      render: (_, feature) => (
        <Space direction="vertical" size={0}>
          <Text strong>{feature.label || feature.name}</Text>
          {feature.description && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {feature.description}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Chiave",
      dataIndex: "name",
      key: "name",
      render: (name) => <Text code>{name}</Text>,
    },
    {
      title: "Piani",
      dataIndex: "plans",
      key: "plans",
      render: (plans) => (
        <Space size="small" wrap>
          {(plans || []).map((plan) => (
            <Tag color={PLAN_COLORS[plan] || "default"} key={plan}>
              {plan}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: (
        <Space>
          Stato
          <Tooltip title="Il toggle sovrascrive il default del piano per questo seller">
            <InfoCircleOutlined />
          </Tooltip>
        </Space>
      ),
      key: "status",
      width: 200,
      render: (_, feature) => {
        const status = getFeatureStatus(feature);
        return (
          <Space>
            <Switch
              checked={status.enabled}
              onChange={(checked) => handleToggle(feature, checked)}
              loading={togglingFeature === feature.objectId}
              checkedChildren={<CheckCircleOutlined />}
              unCheckedChildren={<CloseCircleOutlined />}
            />
            <Tag
              color={
                status.hasOverride
                  ? status.enabled
                    ? "orange"
                    : "red"
                  : status.enabled
                    ? "green"
                    : "default"
              }
            >
              {status.source}
            </Tag>
          </Space>
        );
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (allFeatures.length === 0) {
    return (
      <Card title="Features">
        <Empty
          description={
            <span>
              Nessuna feature nel sistema.{" "}
              <a href="/funeralcloud/features">Crea delle features</a> prima di
              poterle configurare.
            </span>
          }
          style={{ padding: "20px" }}
        />
      </Card>
    );
  }

  return (
    <div>
      <Card
        title={
          <Space>
            Features del Seller
            <Tag>{seller.plan || "base"}</Tag>
          </Space>
        }
        extra={
          <Text type="secondary">
            Piano attuale: <Text strong>{seller.plan || "base"}</Text>
          </Text>
        }
      >
        <Table
          dataSource={allFeatures}
          columns={columns}
          rowKey="objectId"
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );
};

export default MarketplaceSellerFeatures;
